import { Reducer } from 'redux';
import { getBelGeometrySettings } from 'modules/bulkEnquiryLodgement/geometrySettings.utils';
import { BelActionTypes, BelState, INITIAL_BEL_STATE } from './types';

export const BelReducer: Reducer<BelState> = (
  state = INITIAL_BEL_STATE,
  action
) => {
  switch (action.type) {
    case BelActionTypes.INITIATE_BEL_START:
      return {
        ...state,
        loading: true,
        error: null,
        processingPhase: 'SUBMITTING'
      };

    case BelActionTypes.INITIATE_BEL_SUCCESS:
      return {
        ...state,
        loading: false,
        belId: action.payload.belId,
        jobId: action.payload.jobId,
        stripePrice: action.payload.stripePrice,
        processingPhase: 'PROCESSING'
      };

    case BelActionTypes.INITIATE_BEL_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        processingPhase: 'ERROR'
      };

    case BelActionTypes.UPLOAD_FILE_START:
      return {
        ...state,
        loading: true,
        error: null,
        processingPhase: 'UPLOADING_FILE',
        pollMode: null
      };

    case BelActionTypes.UPLOAD_FILE_SUCCESS:
      return {
        ...state,
        loading: true,
        error: null,
        fileInfo: {
          fileId: action.payload.fileId,
          fileName: action.payload.fileName,
          fileSize: action.payload.fileSize,
          polygonCount: 0,
          isValid: false
        }
      };

    case BelActionTypes.UPLOAD_FILE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        processingPhase: 'ERROR'
      };

    case BelActionTypes.TRANSFORM_FILE_START:
      return {
        ...state,
        loading: true,
        error: null,
        processingPhase: 'TRANSFORMING_FILE'
      };

    case BelActionTypes.TRANSFORM_FILE_SUCCESS:
      return {
        ...state,
        belId: action.payload.bulkId,
        jobId: action.payload.jobId,
        processingPhase: 'VALIDATING_FILE'
      };

    case BelActionTypes.TRANSFORM_FILE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        processingPhase: 'ERROR'
      };

    case BelActionTypes.VALIDATE_FILE_START:
      return {
        ...state,
        loading: true,
        processingPhase: 'VALIDATING_FILE'
      };

    case BelActionTypes.VALIDATE_FILE_SUCCESS:
      return {
        ...state,
        loading: false,
        fileInfo: state.fileInfo
          ? {
              ...state.fileInfo,
              polygonCount: action.payload.polygonCount,
              isValid: action.payload.isValid
            }
          : null
      };

    case BelActionTypes.VALIDATE_FILE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        processingPhase: 'ERROR'
      };

    case BelActionTypes.POLL_JOB_START:
      return {
        ...state,
        jobId: action.payload.jobId,
        pollMode: action.payload.pollMode,
        loading: true,
        processingPhase:
          action.payload.pollMode === 'transform'
            ? 'VALIDATING_FILE'
            : 'PROCESSING'
      };

    case BelActionTypes.POLL_JOB_UPDATE:
      return { ...state, jobStatus: action.payload };

    case BelActionTypes.POLL_JOB_COMPLETE:
      return {
        ...state,
        loading: false,
        pollMode: null,
        processingPhase: 'COMPLETED',
        enquiriesLodged: action.payload.enquiriesLodged
      };

    case BelActionTypes.POLL_JOB_FAILURE:
      return {
        ...state,
        loading: false,
        pollMode: null,
        error: action.payload,
        processingPhase: 'ERROR'
      };

    case BelActionTypes.POLL_JOB_TIMEOUT:
      return {
        ...state,
        loading: false,
        pollMode: null,
        error: action.payload,
        processingPhase: 'TIMEOUT'
      };

    case BelActionTypes.FETCH_BEL_SUCCESS: {
      const p = action.payload;
      const validStatuses = ['UPLOADED', 'UPDATED', 'UPDATING', 'COMPLETED'];
      const isValid = validStatuses.includes(p.status);
      const finishedTransform = state.pollMode === 'transform' && isValid;
      return {
        ...state,
        loading: finishedTransform ? false : state.loading,
        error: null,
        pollMode: finishedTransform ? null : state.pollMode,
        bulkStatus: p.status ?? state.bulkStatus,
        originalFileType: p.originalFileType ?? state.originalFileType,
        totalBulkEnquiries: p.totalBulkEnquiries ?? state.totalBulkEnquiries,
        totalEnquiriesCreated:
          p.totalEnquiriesCreated ?? state.totalEnquiriesCreated,
        updatedFileId:
          p.updatedFileId ?? p.UpdatedFile?.id ?? state.updatedFileId,
        fileInfo: state.fileInfo
          ? {
              ...state.fileInfo,
              polygonCount: p.totalBulkEnquiries ?? state.fileInfo.polygonCount,
              isValid
            }
          : state.fileInfo,
        processingPhase: finishedTransform ? 'IDLE' : state.processingPhase
      };
    }

    case BelActionTypes.FETCH_BEL_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        processingPhase: 'ERROR'
      };

    case BelActionTypes.FETCH_LIVE_PRICE_START:
      return {
        ...state,
        stripePrice: null,
        livePriceLoading: true,
        livePriceError: null
      };

    case BelActionTypes.FETCH_LIVE_PRICE_SUCCESS:
      return {
        ...state,
        stripePrice: action.payload,
        livePriceLoading: false,
        livePriceError: null
      };

    case BelActionTypes.FETCH_LIVE_PRICE_FAILURE:
      return {
        ...state,
        livePriceLoading: false,
        livePriceError: action.payload
      };

    case BelActionTypes.SUBMIT_BEL_DETAILS_START:
      return {
        ...state,
        loading: true,
        error: null,
        processingPhase: 'SUBMITTING'
      };

    case BelActionTypes.SUBMIT_BEL_DETAILS_SUCCESS:
      return {
        ...state,
        loading: true,
        processingPhase: 'PROCESSING'
      };

    case BelActionTypes.SUBMIT_BEL_DETAILS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        processingPhase: 'ERROR'
      };

    case BelActionTypes.FETCH_DOWNLOAD_START:
      return { ...state, loading: true, error: null };

    case BelActionTypes.FETCH_DOWNLOAD_SUCCESS:
      return {
        ...state,
        loading: false,
        downloadUrl: action.payload.url
      };

    case BelActionTypes.FETCH_DOWNLOAD_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    case BelActionTypes.CREATE_BEL_CHECKOUT_START:
      return {
        ...state,
        paymentStatus: 'creating',
        error: null
      };

    case BelActionTypes.CREATE_BEL_CHECKOUT_SUCCESS: {
      const { clientSecret, invoiceId, completed } = action.payload;
      const isInvoice = Boolean(invoiceId) || completed === true;
      return {
        ...state,
        paymentStatus: isInvoice ? 'invoice' : 'checkout',
        checkoutClientSecret: clientSecret || null,
        invoiceId: invoiceId || null,
        isPostpay: isInvoice,
        loading: false
      };
    }

    case BelActionTypes.CREATE_BEL_CHECKOUT_FAILURE:
      return {
        ...state,
        paymentStatus: 'failed',
        error: action.payload,
        loading: false
      };

    case BelActionTypes.CONFIRM_BEL_PAYMENT:
      return {
        ...state,
        paymentStatus: 'confirmed',
        loading: true,
        processingPhase: 'PROCESSING'
      };

    case BelActionTypes.VERIFY_BEL_PAYMENT:
      return {
        ...state,
        paymentStatus: 'confirmed',
        loading: true,
        processingPhase: 'PROCESSING',
        currentStep: 3
      };

    case BelActionTypes.VERIFY_BEL_PAYMENT_SUCCESS:
      return {
        ...state,
        transactionReference: action.payload.paymentIntentId ?? null
      };

    case BelActionTypes.RESET_BEL_PAYMENT:
      return {
        ...state,
        paymentStatus: 'idle',
        checkoutClientSecret: null,
        invoiceId: null,
        isPostpay: false,
        transactionReference: null,
        processingPhase: 'IDLE'
      };

    case BelActionTypes.SET_STEP:
      return { ...state, currentStep: action.payload };

    case BelActionTypes.SET_SHAPE_HANDLING:
      return { ...state, shapeHandling: action.payload };

    case BelActionTypes.SET_ENQUIRY_DETAILS:
      return { ...state, enquiryDetails: action.payload };

    case BelActionTypes.SET_VALIDATION_SUMMARY: {
      const geometrySettings = getBelGeometrySettings(
        action.payload.geometryTypes ?? [],
        action.payload.hasPolygonExceeding20Km2
      );
      return {
        ...state,
        hasNonPointGeometry: action.payload.hasNonPointGeometry,
        validationSummary: action.payload,
        bufferDistance: geometrySettings.bufferDefault,
        maxSplitAreaKm: geometrySettings.maxSplitDefault,
        applyBuffer: false,
        applySplit: false,
        geometryPipelineApplied: false,
        geometryPipelineFailed: false,
        geometrySettingsValidation: INITIAL_BEL_STATE.geometrySettingsValidation
      };
    }

    case BelActionTypes.SET_BUFFER_DISTANCE:
      return {
        ...state,
        bufferDistance: action.payload,
        geometryPipelineApplied: false,
        geometryPipelineFailed: false,
        geometrySettingsValidation: {
          ...state.geometrySettingsValidation,
          canProceed: null,
          violations: [],
          error: null
        }
      };

    case BelActionTypes.SET_MAX_SPLIT_AREA:
      return {
        ...state,
        maxSplitAreaKm: action.payload,
        geometryPipelineApplied: false,
        geometryPipelineFailed: false,
        geometrySettingsValidation: {
          ...state.geometrySettingsValidation,
          canProceed: null,
          violations: [],
          error: null
        }
      };

    case BelActionTypes.SET_APPLY_BUFFER:
      return {
        ...state,
        applyBuffer: action.payload,
        geometryPipelineApplied: false,
        geometryPipelineFailed: false,
        geometrySettingsValidation: {
          ...state.geometrySettingsValidation,
          canProceed: null,
          violations: [],
          error: null
        }
      };

    case BelActionTypes.SET_APPLY_SPLIT:
      return {
        ...state,
        applySplit: action.payload,
        geometryPipelineApplied: false,
        geometryPipelineFailed: false,
        geometrySettingsValidation: {
          ...state.geometrySettingsValidation,
          canProceed: null,
          violations: [],
          error: null
        }
      };

    case BelActionTypes.VALIDATE_GEOMETRY_SETTINGS_START:
      return {
        ...state,
        error: action.payload.pendingStepAdvance === true ? null : state.error,
        geometryPipelineFailed:
          action.payload.pendingStepAdvance === true
            ? false
            : state.geometryPipelineFailed,
        geometrySettingsValidation: {
          ...state.geometrySettingsValidation,
          loading: true,
          error: null,
          pendingStepAdvance: action.payload.pendingStepAdvance === true
        }
      };

    case BelActionTypes.VALIDATE_GEOMETRY_SETTINGS_SUCCESS:
      return {
        ...state,
        geometrySettingsValidation: {
          loading: false,
          canProceed: action.payload.canProceed,
          requiresSplit: action.payload.requiresSplit,
          violations: action.payload.violations,
          error: null,
          pendingStepAdvance: false
        }
      };

    case BelActionTypes.VALIDATE_GEOMETRY_SETTINGS_FAILURE:
      return {
        ...state,
        geometrySettingsValidation: {
          ...state.geometrySettingsValidation,
          loading: false,
          canProceed: false,
          error: action.payload,
          pendingStepAdvance: false
        }
      };

    case BelActionTypes.APPLY_GEOMETRY_PIPELINE_START:
      return {
        ...state,
        loading: true,
        error: null,
        geometryPipelineFailed: false,
        processingPhase: 'PROCESSING'
      };

    case BelActionTypes.APPLY_GEOMETRY_PIPELINE_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        geometryPipelineApplied: true,
        geometryPipelineFailed: false,
        processingPhase: 'IDLE'
      };

    case BelActionTypes.APPLY_GEOMETRY_PIPELINE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        geometryPipelineFailed: true,
        processingPhase: 'IDLE'
      };

    case BelActionTypes.CLEAR_GEOMETRY_PIPELINE_ERROR:
      return {
        ...state,
        error: null,
        geometryPipelineFailed: false
      };

    case BelActionTypes.CLEAR_GEOMETRY_SETTINGS_VALIDATION:
      return {
        ...state,
        geometrySettingsValidation: INITIAL_BEL_STATE.geometrySettingsValidation
      };

    case BelActionTypes.RESET_BEL:
      return {
        ...INITIAL_BEL_STATE,
        stripePrice: state.stripePrice,
        livePriceLoading: false,
        livePriceError: null
      };

    default:
      return state;
  }
};
